import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage, useAuth, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import StatTile from '../components/ui/StatTile';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  ChefHat, Plus, Loader2, Search, Trash2, Edit3, Save, X,
  Zap, Flame, Check, UtensilsCrossed, PlusCircle, MinusCircle,
  ShoppingCart, Clock, Lightbulb, CheckSquare, Square, ScanBarcode
} from 'lucide-react';
import { RecipeBarcodeScannerButton } from '../components/RecipeBarcodeScanner';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORIES = [
  { key: 'standard', label: 'Standard', color: 'text-primary' },
  { key: 'dejeuner', label: 'Déjeuner', color: 'text-amber-400' },
  { key: 'diner', label: 'Dîner', color: 'text-orange-400' },
  { key: 'souper', label: 'Souper', color: 'text-red-400' },
  { key: 'collation', label: 'Collation', color: 'text-blue-400' },
];

const EMPTY_INGREDIENT = { name: '', portion_g: 100, protein_g: 0, calories: 0, carbs_g: 0, fats_g: 0, fiber_g: 0, is_hidden: false };

export default function MyRecipes() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('all');
  const [calcLoading, setCalcLoading] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [mealPrepLoading, setMealPrepLoading] = useState(false);
  const [mealPrepData, setMealPrepData] = useState(null);
  const [showMealPrep, setShowMealPrep] = useState(false);

  const [form, setForm] = useState({
    name: '', notes: '', is_homemade: true, meal_type: 'repas', category: 'standard',
    ingredients: [{ ...EMPTY_INGREDIENT }],
  });

  // Auto-fill from barcode scan URL params
  useEffect(() => {
    if (searchParams.get('autofill') === '1') {
      const name = searchParams.get('name') ?? '';
      const calories = parseFloat(searchParams.get('calories')) || 0;
      const protein_g = parseFloat(searchParams.get('protein_g')) || 0;
      const carbs_g = parseFloat(searchParams.get('carbs_g')) || 0;
      const fats_g = parseFloat(searchParams.get('fats_g')) || 0;
      const fiber_g = parseFloat(searchParams.get('fiber_g')) || 0;

      setForm({
        name,
        notes: searchParams.get('barcode') ? `Barcode: ${searchParams.get('barcode')}` : '',
        is_homemade: false,
        meal_type: 'repas',
        category: 'standard',
        ingredients: [{
          name: name,
          portion_g: 100,
          calories,
          protein_g,
          carbs_g,
          fats_g,
          fiber_g,
          is_hidden: false,
        }],
      });
      setShowForm(true);
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const fetchRecipes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCat !== 'all') params.append('category', filterCat);
      const res = await api.get(`/user-recipes?${params}`);
      setRecipes(res.data.recipes || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterCat]);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

  const resetForm = () => {
    setForm({ name: '', notes: '', is_homemade: true, meal_type: 'repas', category: 'standard', ingredients: [{ ...EMPTY_INGREDIENT }] });
    setEditingRecipe(null);
  };

  const openEdit = (recipe) => {
    setForm({
      name: recipe.name,
      notes: recipe.notes || '',
      is_homemade: recipe.is_homemade,
      meal_type: recipe.meal_type || 'repas',
      category: recipe.category || 'standard',
      ingredients: recipe.ingredients?.length > 0 ? recipe.ingredients : [{ ...EMPTY_INGREDIENT }],
    });
    setEditingRecipe(recipe);
    setShowForm(true);
  };

  const addIngredient = () => setForm(f => ({ ...f, ingredients: [...f.ingredients, { ...EMPTY_INGREDIENT }] }));
  const removeIngredient = (idx) => setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));
  const updateIngredient = (idx, field, value) => {
    setForm(f => ({
      ...f,
      ingredients: f.ingredients.map((ing, i) =>
        i === idx ? { ...ing, [field]: ['name'].includes(field) ? value : (Number(value) || 0) } : ing
      ),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Nom requis');
    setSaving(true);
    try {
      if (editingRecipe) {
        await api.put(`/user-recipes/${editingRecipe.recipe_id}`, form);
        toast.success('Recette mise à jour!');
      } else {
        await api.post('/user-recipes', form);
        toast.success('Recette créée!');
      }
      setShowForm(false);
      resetForm();
      // Reset filters to ensure newly created recipe is visible immediately
      setFilterCat('all');
      setSearchQuery('');
      fetchRecipes();
    } catch (err) {
      // 🔴 FIX: surface the real backend error instead of silently closing.
      console.error('[MyRecipes] Save failed:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let message;
      if (status === 422 && Array.isArray(detail)) {
        // Pydantic validation: show missing/invalid fields
        const fields = detail.map(d => (d.loc || []).filter(x => x !== 'body').join('.')).filter(Boolean);
        message = fields.length
          ? `Champs invalides: ${fields.join(', ')}`
          : 'Données invalides — vérifie tous les champs.';
      } else if (status === 401 || status === 403) {
        message = 'Session expirée. Reconnecte-toi puis réessaie.';
      } else if (status >= 500) {
        message = 'Erreur serveur. Réessaie dans un instant.';
      } else if (!navigator.onLine) {
        message = 'Pas de connexion internet.';
      } else {
        message = typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde.';
      }
      toast.error(message, { duration: 6000 });
      // Do NOT close the modal on error — keep user's work intact
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (recipeId) => {
    if (!window.confirm('Supprimer cette recette?')) return;
    try {
      await api.delete(`/user-recipes/${recipeId}`);
      toast.success('Recette supprimée');
      fetchRecipes();
    } catch {
      toast.error('Erreur');
    }
  };

  const calcMacrosAuto = async () => {
    const validIngredients = form.ingredients.filter(i => i.name.trim());
    if (validIngredients.length === 0) return toast.error('Ajoute au moins un ingrédient avec un nom');
    setCalcLoading(true);
    try {
      const res = await api.post('/user-recipes/calc-macros', {
        ingredients: validIngredients.map(i => ({ name: i.name, portion_g: i.portion_g || 100 })),
      });
      if (res.data.success && res.data.ingredients) {
        const updated = [...form.ingredients];
        res.data.ingredients.forEach((ai, idx) => {
          if (idx < updated.length) {
            updated[idx] = {
              ...updated[idx],
              calories: Math.round(ai.calories || 0),
              protein_g: Math.round((ai.protein_g || 0) * 10) / 10,
              carbs_g: Math.round((ai.carbs_g || 0) * 10) / 10,
              fats_g: Math.round((ai.fats_g || 0) * 10) / 10,
              fiber_g: Math.round((ai.fiber_g || 0) * 10) / 10,
              portion_g: ai.portion_g || updated[idx].portion_g,
            };
          }
        });
        setForm(f => ({ ...f, ingredients: updated }));
        toast.success('Macros calculées par l\'IA!');
      }
    } catch {
      toast.error('Erreur de calcul');
    } finally {
      setCalcLoading(false);
    }
  };

  const toggleSelect = (recipeId) => {
    setSelectedIds(prev =>
      prev.includes(recipeId) ? prev.filter(id => id !== recipeId) : [...prev, recipeId]
    );
  };

  const generateMealPrep = async () => {
    if (selectedIds.length < 2) return toast.error('Sélectionne au moins 2 recettes');
    if (selectedIds.length > 7) return toast.error('Maximum 7 recettes');
    setMealPrepLoading(true);
    try {
      const res = await api.post('/user-recipes/meal-prep', { recipe_ids: selectedIds });
      if (res.data.success) {
        setMealPrepData(res.data);
        setShowMealPrep(true);
        setSelectMode(false);
        setSelectedIds([]);
      }
    } catch (err) {
      toast.error('Erreur de génération du meal prep');
    } finally {
      setMealPrepLoading(false);
    }
  };

  const totalMacros = form.ingredients.reduce((acc, i) => ({
    calories: acc.calories + (i.calories || 0),
    protein_g: acc.protein_g + (i.protein_g || 0),
    carbs_g: acc.carbs_g + (i.carbs_g || 0),
    fats_g: acc.fats_g + (i.fats_g || 0),
  }), { calories: 0, protein_g: 0, carbs_g: 0, fats_g: 0 });

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-3xl mx-auto space-y-6" data-testid="my-recipes-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-black uppercase tracking-tight text-foreground">
              Mes Recettes
            </h1>
            <p className="text-sm text-muted-foreground">
              {recipes.length} recette{recipes.length !== 1 ? 's' : ''} enregistrée{recipes.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            {recipes.length >= 2 && (
              <Button
                variant={selectMode ? 'default' : 'outline'}
                size="sm"
                className={selectMode
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'border-red-500/40 text-red-400 hover:bg-red-500/10'
                }
                onClick={() => { setSelectMode(!selectMode); setSelectedIds([]); }}
                data-testid="meal-prep-select-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-1.5" />
                {selectMode ? 'Annuler' : 'MEAL PREP'}
              </Button>
            )}
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="create-recipe-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer
            </Button>
          </div>
        </div>

        {/* Selection Mode Bar */}
        <AnimatePresence>
          {selectMode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex items-center justify-between p-3 rounded-xl border-2 border-red-500/40 bg-red-500/10" data-testid="meal-prep-selection-bar">
                <p className="text-sm text-foreground">
                  <span className="font-bold text-red-400">{selectedIds.length}</span> recette{selectedIds.length !== 1 ? 's' : ''} sélectionnée{selectedIds.length !== 1 ? 's' : ''} (2-7)
                </p>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
                  disabled={selectedIds.length < 2 || mealPrepLoading}
                  onClick={generateMealPrep}
                  data-testid="generate-meal-prep-btn"
                >
                  {mealPrepLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />}
                  {mealPrepLoading ? 'Génération...' : 'Générer le Plan'}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search + Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Chercher une recette..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="recipe-search-input"
            />
          </div>
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          <button
            onClick={() => setFilterCat('all')}
            className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${filterCat === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'}`}
          >
            Toutes
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setFilterCat(cat.key)}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${filterCat === cat.key ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Recipes Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : recipes.length === 0 ? (
          <Card className="border-dashed border-2 border-border">
            <CardContent className="py-12 text-center">
              <ChefHat className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-muted-foreground text-sm">
                {searchQuery ? 'Aucune recette trouvée' : 'Aucune recette enregistrée'}
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Crée ta première recette pour un log rapide en 1 clic!
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 border-primary/30 text-primary"
                onClick={() => { resetForm(); setShowForm(true); }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Créer ma première recette
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <AnimatePresence>
              {recipes.map((recipe, idx) => (
                <motion.div
                  key={recipe.recipe_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Card
                    className={`border-border/50 hover:border-primary/30 transition-all group cursor-pointer ${
                      selectMode && selectedIds.includes(recipe.recipe_id) ? 'ring-2 ring-red-500 border-red-500/50' : ''
                    }`}
                    data-testid={`recipe-card-${recipe.recipe_id}`}
                    onClick={() => selectMode ? toggleSelect(recipe.recipe_id) : openEdit(recipe)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          {selectMode && (
                            <div className="mt-0.5 shrink-0">
                              {selectedIds.includes(recipe.recipe_id)
                                ? <CheckSquare className="w-5 h-5 text-red-400" />
                                : <Square className="w-5 h-5 text-muted-foreground" />
                              }
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-bold text-foreground text-sm truncate group-hover:text-primary transition-colors">
                            {recipe.name}
                          </h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${
                              CATEGORIES.find(c => c.key === recipe.category)?.color || 'text-muted-foreground'
                            } bg-secondary/50`}>
                              {CATEGORIES.find(c => c.key === recipe.category)?.label || recipe.category}
                            </span>
                            {recipe.is_homemade && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-bold">
                                MAISON
                              </span>
                            )}
                            {recipe.usage_count > 0 && (
                              <span className="text-[10px] text-muted-foreground">{recipe.usage_count}x logué</span>
                            )}
                          </div>
                        </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(recipe.recipe_id); }}
                            className="p-1 rounded hover:bg-red-500/20 text-red-400"
                            data-testid={`delete-recipe-${recipe.recipe_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {/* Macro bar */}
                      <div className="grid grid-cols-4 gap-1 mt-2">
                        <div className="text-center p-1.5 rounded bg-secondary/30">
                          <p className="text-xs font-black text-orange-400">{Math.round(recipe.total_calories || 0)}</p>
                          <p className="text-[9px] text-muted-foreground">cal</p>
                        </div>
                        <div className="text-center p-1.5 rounded bg-secondary/30">
                          <p className="text-xs font-black text-blue-400">{Math.round(recipe.total_protein_g || 0)}g</p>
                          <p className="text-[9px] text-muted-foreground">prot</p>
                        </div>
                        <div className="text-center p-1.5 rounded bg-secondary/30">
                          <p className="text-xs font-black text-amber-400">{Math.round(recipe.total_carbs_g || 0)}g</p>
                          <p className="text-[9px] text-muted-foreground">carbs</p>
                        </div>
                        <div className="text-center p-1.5 rounded bg-secondary/30">
                          <p className="text-xs font-black text-red-400">{Math.round(recipe.total_fats_g || 0)}g</p>
                          <p className="text-[9px] text-muted-foreground">gras</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Create/Edit Recipe Dialog */}
        <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); setShowForm(open); }}>
          <DialogContent className="sm:max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase text-primary flex items-center gap-2">
                <ChefHat className="w-5 h-5" />
                {editingRecipe ? 'Modifier la recette' : 'Nouvelle recette standard'}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                Enregistre tes bases pour un log en 1 clic.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Name */}
              <Input
                placeholder="Nom de la recette (ex: Vinaigrette maison)"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="recipe-name-input"
                className="font-medium"
              />

              {/* Category + Homemade toggle */}
              <div className="flex gap-2">
                <select
                  className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={form.category}
                  onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                  data-testid="recipe-category-select"
                >
                  {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <Button
                  variant={form.is_homemade ? 'default' : 'outline'}
                  size="sm"
                  className={`flex-shrink-0 ${form.is_homemade ? 'bg-green-600 hover:bg-green-700' : 'border-border'}`}
                  onClick={() => setForm(f => ({ ...f, is_homemade: !f.is_homemade }))}
                  data-testid="homemade-toggle"
                >
                  {form.is_homemade ? <Check className="w-4 h-4 mr-1" /> : null}
                  Maison
                </Button>
              </div>

              {/* Ingredients */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Ingrédients</p>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-primary" onClick={addIngredient} data-testid="add-ingredient-btn">
                    <PlusCircle className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>
                {form.ingredients.map((ing, idx) => (
                  <div key={idx} className="p-2.5 rounded-lg border border-border/50 bg-secondary/20 space-y-2" data-testid={`ingredient-row-${idx}`}>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Nom (ex: Poulet grillé)"
                        value={ing.name}
                        onChange={(e) => updateIngredient(idx, 'name', e.target.value)}
                        className="h-8 text-xs flex-1"
                      />
                      {/* Barcode scanner UI hidden — Yuri spec Iter. 321. Re-enable in 1 line by removing the `false &&` gate. */}
                      {false && (
                        <RecipeBarcodeScannerButton
                          language={language}
                          onProductFound={(product) => {
                            const updated = [...form.ingredients];
                            updated[idx] = {
                              ...updated[idx],
                              name: product.name + (product.brand ? ` (${product.brand})` : ''),
                              portion_g: product.portion_g || 100,
                              calories: product.calories || 0,
                              protein_g: product.protein_g || 0,
                              carbs_g: product.carbs_g || 0,
                              fats_g: product.fats_g || 0,
                              fiber_g: product.fiber_g || 0,
                            };
                            setForm(f => ({ ...f, ingredients: updated }));
                            toast.success(`${product.name} ajouté!`);
                          }}
                        />
                      )}
                      <button onClick={() => removeIngredient(idx)} className="p-1 text-red-400 hover:bg-red-500/10 rounded" disabled={form.ingredients.length <= 1}>
                        <MinusCircle className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-5 gap-1.5">
                      <div>
                        <label className="text-[9px] text-muted-foreground">Portion g</label>
                        <Input type="number" value={ing.portion_g} onChange={(e) => updateIngredient(idx, 'portion_g', e.target.value)} className="h-7 text-xs" />
                      </div>
                      <div>
                        <label className="text-[9px] text-muted-foreground">Cal</label>
                        <Input type="number" value={ing.calories} onChange={(e) => updateIngredient(idx, 'calories', e.target.value)} className="h-7 text-xs" />
                      </div>
                      <div>
                        <label className="text-[9px] text-muted-foreground">Prot g</label>
                        <Input type="number" value={ing.protein_g} onChange={(e) => updateIngredient(idx, 'protein_g', e.target.value)} className="h-7 text-xs" />
                      </div>
                      <div>
                        <label className="text-[9px] text-muted-foreground">Carbs g</label>
                        <Input type="number" value={ing.carbs_g} onChange={(e) => updateIngredient(idx, 'carbs_g', e.target.value)} className="h-7 text-xs" />
                      </div>
                      <div>
                        <label className="text-[9px] text-muted-foreground">Gras g</label>
                        <Input type="number" value={ing.fats_g} onChange={(e) => updateIngredient(idx, 'fats_g', e.target.value)} className="h-7 text-xs" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Auto Macro Calculator button */}
              <Button
                variant="outline"
                className="w-full border-primary/40 text-primary hover:bg-primary/10"
                onClick={calcMacrosAuto}
                disabled={calcLoading || form.ingredients.every(i => !i.name.trim())}
                data-testid="calc-macros-btn"
              >
                {calcLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                {calcLoading ? 'Analyse en cours...' : 'Calculer les macros (IA)'}
              </Button>

              {/* Live macro totals — GLOW (Iter. 388) */}
              <div className="grid grid-cols-4 gap-2" data-testid="macro-totals">
                <StatTile theme="orange" value={Math.round(totalMacros.calories)}      label="Calories"  compact />
                <StatTile theme="blue"   value={`${Math.round(totalMacros.protein_g)}g`} label="Protéines" compact />
                <StatTile theme="amber"  value={`${Math.round(totalMacros.carbs_g)}g`}   label="Carbs"     compact />
                <StatTile theme="red"    value={`${Math.round(totalMacros.fats_g)}g`}    label="Gras"      compact />
              </div>

              {/* Notes */}
              <Textarea
                placeholder="Notes (optionnel)"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />

              {/* Save */}
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={!form.name.trim() || saving}
                onClick={handleSave}
                data-testid="save-recipe-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingRecipe ? 'Mettre à jour' : 'Enregistrer la recette'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        {/* Meal Prep Results Dialog */}
        <Dialog open={showMealPrep} onOpenChange={setShowMealPrep}>
          <DialogContent className="sm:max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase text-red-400 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Plan d'Attaque — Meal Prep
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                {mealPrepData?.recipe_count || 0} recettes • {mealPrepData?.total_prep_time_min || '?'} min de préparation
              </DialogDescription>
            </DialogHeader>

            {mealPrepData && (
              <div className="space-y-5 pt-2">
                {/* Recipes selected */}
                <div className="flex flex-wrap gap-1.5">
                  {mealPrepData.recipes?.map((name, i) => (
                    <span key={i} className="px-2 py-1 rounded-full text-[10px] font-bold bg-red-500/15 text-red-400 border border-red-500/20">
                      {name}
                    </span>
                  ))}
                </div>

                {/* 1. Liste de Ravitaillement */}
                <div className="space-y-2" data-testid="meal-prep-grocery-list">
                  <h3 className="font-heading text-sm uppercase tracking-wider text-foreground flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-red-400" />
                    Liste de Ravitaillement
                  </h3>
                  {mealPrepData.grocery_list?.map((aisle, i) => (
                    <div key={i} className="p-3 rounded-xl border border-border/50 bg-secondary/20">
                      <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2">{aisle.aisle}</p>
                      <div className="grid grid-cols-2 gap-1">
                        {aisle.items?.map((item, j) => (
                          <div key={j} className="flex items-center gap-1.5 text-xs text-foreground/80">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                            <span>{item.name}</span>
                            <span className="text-muted-foreground ml-auto">{item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* 2. Plan d'Attaque */}
                <div className="space-y-2" data-testid="meal-prep-cooking-plan">
                  <h3 className="font-heading text-sm uppercase tracking-wider text-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-400" />
                    Séquençage Tactique
                  </h3>
                  {mealPrepData.cooking_plan?.map((step, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex gap-3 p-3 rounded-xl border border-border/50 bg-secondary/20"
                    >
                      <span className="w-7 h-7 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center text-xs font-black shrink-0">
                        {step.step}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm text-foreground">{step.action}</p>
                        <div className="flex items-center gap-3 mt-1">
                          {step.duration_min > 0 && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {step.duration_min} min
                            </span>
                          )}
                          {step.tip && (
                            <span className="text-[10px] text-amber-400 flex items-center gap-1">
                              <Lightbulb className="w-3 h-3" /> {step.tip}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* 3. Bilan des Munitions */}
                <div data-testid="meal-prep-macro-totals">
                  <h3 className="font-heading text-sm uppercase tracking-wider text-foreground flex items-center gap-2 mb-2">
                    <Flame className="w-4 h-4 text-orange-400" />
                    Bilan des Munitions (Total Batch)
                  </h3>
                  <div className="grid grid-cols-4 gap-2 p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border-2 border-red-500/30">
                    <div className="text-center">
                      <p className="text-2xl font-black text-orange-400">{Math.round(mealPrepData.macro_totals?.calories || 0)}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Calories</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-black text-blue-400">{Math.round(mealPrepData.macro_totals?.protein_g || 0)}g</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Protéines</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-black text-amber-400">{Math.round(mealPrepData.macro_totals?.carbs_g || 0)}g</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Carbs</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-black text-red-400">{Math.round(mealPrepData.macro_totals?.fats_g || 0)}g</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Lipides</p>
                    </div>
                  </div>
                </div>

                {/* 4. Pro Tips */}
                {mealPrepData.pro_tips?.length > 0 && (
                  <div className="space-y-1.5">
                    {mealPrepData.pro_tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Lightbulb className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
